
import Vue from 'vue';
import {
	calcEnterpriseTokenUsage,
	calcRunnitTokensDataForBalanceAccount,
	DbTimestamp,
	EnterpriseTokenUsage,
} from '@run-diffusion/shared';
import { deriveEnterpriseUsageCaption, EnterpriseUsageCaption } from '@/utils/tokenBuckets';
import ReportingAnalyticsCard from '@/views/Team/components/ReportingAnalyticsCard.vue';
import MetricAnalyticsCard from '@/views/Team/components/MetricAnalyticsCard.vue';
import RunnitTokens from '@/views/Runnits/RunnitTokens.vue';
import { mapGetters, mapState, mapActions } from 'vuex';
import { getMonthlyResetDisplayText } from '@/utils/timeUtils';
import { TeamUserMixin } from '@/mixins';
import IsBoundToBudgetBadge from '@/views/Team/components/IsBoundToBudgetBadge.vue';

export default Vue.extend({
	name: 'TeamMetricsBtns',
	mixins: [TeamUserMixin],
	props: {
		currTeam: { type: Object, default: null },
		currTeamUserRole: { type: Object, default: null },
		currBalanceAccount: { type: Object, default: null },
		loadingBalanceAccount: { type: Boolean, default: false },
	},
	computed: {
		...mapState([
			'team',
			'realTimeMoment',
		]),
		...mapGetters([
			'displayLabels',
			'companyClubOfferBenefits',
		]),
		/*
		Who has joined this Team, read off the Team's own live document: its `userIds` is the
		membership the rules check, one entry per joined member. The member list below pages
		itself from Postgres, so the page no longer loads every membership to count them.
		 */
		acceptedMembersCount (): number {
			const userIds: Record<string, boolean> = (this.currTeam && this.currTeam.userIds) || {};
			return Object.keys(userIds).filter((userId: string) => userIds[userId] === true).length;
		},
		// Permission checks must run against the team this page is showing (route teamId),
		// not the signed-in team in the store.
		hasCurrTeamShowTokenBalancePermission (): boolean {
			if (!this.currTeam) return false;
			return this.checkShowTokenBalancePermission(this.currTeam, this.currTeamUserRole);
		},
		runnitTokens () {
			if (!this.currBalanceAccount) return 0;
			return calcRunnitTokensDataForBalanceAccount(this.currBalanceAccount).runnitTokens;
		},
		isEnterprise (): boolean {
			return !!this.companyClubOfferBenefits.isEnterprise;
		},
		/*
		Enterprise teams count UP. Their balance is reset to the budget each month and can go
		negative, so the signed number is not what an admin wants to read: the hero is what this cycle
		spent, through the same definition the Runnits side-nav shows the team, and the caption says
		where that leaves the budget. This page's own account and team, not the store's active ones.
		 */
		enterpriseUsage (): EnterpriseTokenUsage {
			return calcEnterpriseTokenUsage(this.currBalanceAccount, this.currTeam);
		},
		heroTokens (): number {
			return this.isEnterprise ? this.enterpriseUsage.used : this.runnitTokens;
		},
		usageCaption (): EnterpriseUsageCaption {
			if (!this.isEnterprise) return { text: 'Current token balance', danger: false };
			return deriveEnterpriseUsageCaption({
				usage: this.enterpriseUsage,
				budget: this._get(this.currTeam, 'allotmentSettings.monthlyRunnitTokensBudget') || 0,
				teamLabel: this.displayLabels.team,
			});
		},
		currCompany () {
			return this.currTeam && typeof this.currTeam.company !== 'string' ? this.currTeam.company : null;
		},
		resetDisplayText () {
			const renewAt: DbTimestamp = this.currCompany?.monthlyBenefitsRenewAt || null;
			if (!renewAt) return '';
			return getMonthlyResetDisplayText(renewAt, this.realTimeMoment, { isShortened: true });
		},
	},
	methods: {
		...mapActions([
			'setUpsellDialog',
		]),
		onEnterpriseReportsUpsell () {
			this.setUpsellDialog({ noCompanyReports: true });
		},
		onEnterpriseBudgetsUpsell () {
			this.setUpsellDialog({ noCompanyBudgets: true });
		},
	},
	components: {
		ReportingAnalyticsCard,
		MetricAnalyticsCard,
		RunnitTokens,
		IsBoundToBudgetBadge,
	},
});
